import { useEffect, useState } from 'react';
import { Contest } from '../../models/types';
import { apiService } from '../../services/api.service';
import i18n from '../../i18n/i18n-setup';

// One ranked entry by distance: a team in team contests, an athlete in individual ones.
export interface StandingEntry {
  id: string;
  name: string;
  subtitle?: string;
  totalDistance: number;
  rank: number;
}

// Live distance standings from the leaderboard endpoints. The distance stored on team
// records is not kept up to date, so totals and ranks must come from here instead.
export const useContestStandings = (contest: Contest | null, refreshKey: number) => {
  const [standings, setStandings] = useState<StandingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const contestId = contest?._id;
  const isTeam = contest?.contestType === 'Team';

  useEffect(() => {
    if (!contestId) return;
    let cancelled = false;

    const load = async (): Promise<StandingEntry[]> => {
      if (isTeam) {
        const res = await apiService.getContestTeamLeaderboard(contestId, 'totalDistance', 1000);
        return (res.teams || []).map((t: any) => ({
          id: t.teamId,
          name: t.teamName,
          subtitle: i18n.t('contests:teamStats.memberCount', { count: Number(t.memberCount) || 0 }),
          totalDistance: Number(t.totalDistance) || 0,
          rank: t.rank,
        }));
      }
      // Same ordering as the individual leaderboard table: distance, then active days.
      const entries = await apiService.getIndividualContestLeaderboard(contestId);
      return [...entries]
        .sort((a, b) => b.totalDistance - a.totalDistance || b.totalTracklog - a.totalTracklog)
        .map((e, i) => ({
          id: e.userId.toString(),
          name: e.userDetails?.name || e.userId.toString(),
          subtitle: e.userDetails?.username ? `@${e.userDetails.username}` : undefined,
          totalDistance: Number(e.totalDistance) || 0,
          rank: i + 1,
        }));
    };

    setLoading(true);
    load()
      .then(next => !cancelled && setStandings(next))
      .catch(error => {
        console.error('Error fetching contest standings:', error);
        if (!cancelled) setStandings([]);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [contestId, isTeam, refreshKey]);

  return { standings, loading };
};
